import * as React from 'react';
import {FC} from 'react';
import {renderItemLink, renderLpnLink, renderShipment} from '../../../libs/helpers';
import {getQTYUoM} from '../../shared/utilities/DataFormatting';
import CustomKebabMenu, {KebabMenuOption} from '../../shared/CustomKebabMenu';
import {ManifestContents, ManifestLpn} from './ManifestInterfaces';
import LpnContentPaginationButtons from './LpnContentPaginationButtons';
import {ManifestLpnConfirmRemoveModal} from './ManifestLpnConfirmRemoveModal';

interface Props {
  manifestLpns: ManifestLpn[];
  manifestLpnsCount: number;
  pageSize: number;
  currentManifestLpnsPage: number;
  authenticityToken: string;
  allowEditing: boolean;
  handlePagination(page);
  setRefreshLpns(value);
}

export const ManifestLpns: FC<Props> = (props) => {
  const [emptyMessage, setEmptyMessage] = React.useState<string>(Messages.NoResults);
  const [showingLpnRemoveConfirmModal, setShowingLpnRemoveConfirmModal] = React.useState<boolean>(false);
  const [lpnBeingEdited, setLpnBeingEdited] = React.useState<ManifestLpn>(null);

  const modalParams: LpnModalParams = {
    setLpnBeingEdited,
    setShowingLpnRemoveConfirmModal
  };

  return (
    <React.Fragment>
      <div className="lpn-container">
        {props.manifestLpns.length > props.pageSize && (
          <div className="row vertical-align pull-right space-below">
            <LpnContentPaginationButtons
              currentPage={props.currentManifestLpnsPage}
              pageSize={props.pageSize}
              setCurrentPage={props.handlePagination}
              lpnCount={props.manifestLpnsCount}
            />
          </div>
        )}
        <table className="table">
          <thead>
            <tr>
              <th className="lpn-column">{Headers.LPNs}</th>
              <th className="shipmentid-column">{Headers.SHIPMENTID}</th>
              <th className="sku-column">{Headers.SKUs}</th>
              <th className="qty-column">{Headers.QTYUoM}</th>
              {props.allowEditing && <th className="edit-column"></th>}
            </tr>
          </thead>
          <tbody>
            {props.manifestLpns.map((manifestLpn, manifestLpnIdx) =>
              lpnRow(manifestLpn, manifestLpnIdx, modalParams, props.allowEditing)
            )}
          </tbody>
        </table>
        {props.manifestLpns.length === 0 && <span>{emptyMessage}</span>}
      </div>
      <ManifestLpnConfirmRemoveModal
        show={showingLpnRemoveConfirmModal}
        lpn={lpnBeingEdited}
        manifestId={lpnBeingEdited?.outbound_manifest_id}
        toggleModal={() => {
          setShowingLpnRemoveConfirmModal(false);
          setLpnBeingEdited(null);
          props.setRefreshLpns(true);
        }}
        authenticityToken={props.authenticityToken}
      />
    </React.Fragment>
  );
};

const lpnRow = (manifestLpn: ManifestLpn, manifestLpnIdx, modalParams: LpnModalParams, allowEditing) => {
  return (
    <React.Fragment key={manifestLpnIdx}>
      <tr>
        <td className="lpn-column">{renderLpnLink(manifestLpn.lpn_barcode, false)}</td>
        <td className="shipmentid-column">{renderShipment(manifestLpn.shipment_id, false, false, true, true)}</td>
        <td className="lpn-contents-cell" colSpan={2}>
          <table className="lpn-contents">
            <tbody>
              {manifestLpn.manifest_contents.map((content, contentIdx) => lpnContentRow(content, contentIdx))}
            </tbody>
          </table>
        </td>
        {allowEditing && manifestLpn.parent_lpn_detail_id === null && (
          <td className="kebab-container">
            <CustomKebabMenu options={ellipsisMenuOptions(manifestLpn, modalParams)} displayBeside />
          </td>
        )}
      </tr>
    </React.Fragment>
  );
};

const lpnContentRow = (content: ManifestContents, contentIdx) => {
  return (
    <React.Fragment key={contentIdx}>
      <tr>
        <td className="sku-column">{renderItemLink(content.inventory_id, content.sku, false)}</td>
        <td className="qty-column">{getQTYUoM(content.quantity, content.unit_of_measure)}</td>
      </tr>
    </React.Fragment>
  );
};

interface LpnModalParams {
  setLpnBeingEdited: (lpnBeingEdited: ManifestLpn) => void;
  setShowingLpnRemoveConfirmModal: (state: boolean) => void;
}

const ellipsisMenuOptions = (manifestLpn: ManifestLpn, modalParams: LpnModalParams) => {
  const menuOptions: KebabMenuOption[] = [];

  menuOptions.push({
    optionText: 'Remove LPN from Load',
    isActionDangerous: true,
    optionAction: () => {
      modalParams.setLpnBeingEdited(manifestLpn);
      modalParams.setShowingLpnRemoveConfirmModal(true);
    }
  });

  return menuOptions;
};

enum Headers {
  LPNs = 'LPN',
  SHIPMENTID = 'Shipment ID',
  SKUs = 'SKUs',
  QTYUoM = 'Quantity'
}

enum Messages {
  NoResults = 'No LPNs currently attached to this load'
}
